import { createContext, useContext, ReactNode } from 'react';
import { cn } from '../lib/utils';

// DataTable Context
interface DataTableContextType {
  variant?: 'default' | 'striped';
  size?: 'sm' | 'md' | 'lg';
}

const DataTableContext = createContext<DataTableContextType>({});

// Main DataTable Component
interface DataTableProps {
  children: ReactNode;
  variant?: 'default' | 'striped';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const DataTable = ({ children, variant = 'default', size = 'md', className }: DataTableProps) => {
  return (
    <DataTableContext.Provider value={{ variant, size }}>
      <div className={cn('overflow-x-auto', className)}>
        <table className="w-full">
          {children}
        </table>
      </div>
    </DataTableContext.Provider>
  );
};

// Header Component
interface DataTableHeaderProps {
  children: ReactNode;
  className?: string;
}

const DataTableHeader = ({ children, className }: DataTableHeaderProps) => {
  return (
    <thead className={cn(
      'bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600',
      className
    )}>
      {children}
    </thead>
  );
};

// Body Component
interface DataTableBodyProps {
  children: ReactNode;
  className?: string;
}

const DataTableBody = ({ children, className }: DataTableBodyProps) => {
  const { variant } = useContext(DataTableContext);
  
  return (
    <tbody className={cn(
      'bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700',
      variant === 'striped' && '[&>tr:nth-child(even)]:bg-gray-50 dark:[&>tr:nth-child(even)]:bg-gray-700/50',
      className
    )}>
      {children}
    </tbody>
  );
};

// Row Component
interface DataTableRowProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  hoverable?: boolean;
}

const DataTableRow = ({ children, className, onClick, hoverable = true }: DataTableRowProps) => {
  return (
    <tr 
      className={cn(
        hoverable && 'hover:bg-gray-50 dark:hover:bg-gray-700',
        onClick && 'cursor-pointer',
        'transition-colors',
        className
      )}
      onClick={onClick}
    >
      {children}
    </tr>
  );
};

// Header Cell Component
interface DataTableHeaderCellProps {
  children: ReactNode;
  className?: string;
  sortable?: boolean;
  onSort?: () => void;
  sortDirection?: 'asc' | 'desc' | null;
}

const DataTableHeaderCell = ({ 
  children, 
  className, 
  sortable = false, 
  onSort,
  sortDirection 
}: DataTableHeaderCellProps) => {
  const { size } = useContext(DataTableContext);
  
  const paddingClasses = {
    sm: 'px-3 py-2',
    md: 'px-4 py-3',
    lg: 'px-6 py-4'
  };

  return (
    <th className={cn(
      paddingClasses[size || 'md'],
      'text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider',
      sortable && 'cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-100',
      className
    )}
    onClick={sortable ? onSort : undefined}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortable && (
          <span className="text-gray-400">
            {sortDirection === 'asc' ? '↑' : sortDirection === 'desc' ? '↓' : '↕'}
          </span>
        )}
      </div>
    </th>
  );
};

// Cell Component
interface DataTableCellProps {
  children: ReactNode;
  className?: string;
  minWidth?: string;
}

const DataTableCell = ({ children, className, minWidth }: DataTableCellProps) => {
  const { size } = useContext(DataTableContext);
  
  const paddingClasses = {
    sm: 'px-3 py-2',
    md: 'px-4 py-3',
    lg: 'px-6 py-4'
  };

  return (
    <td 
      className={cn(
        paddingClasses[size || 'md'],
        'text-sm text-gray-900 dark:text-gray-100',
        className
      )}
      style={minWidth ? { minWidth } : undefined}
    >
      {children}
    </td>
  );
};

// Compound exports
DataTable.Header = DataTableHeader;
DataTable.Body = DataTableBody;
DataTable.Row = DataTableRow;
DataTable.HeaderCell = DataTableHeaderCell;
DataTable.Cell = DataTableCell;

export { DataTable };
export type {
  DataTableProps,
  DataTableHeaderProps,
  DataTableBodyProps,
  DataTableRowProps,
  DataTableHeaderCellProps,
  DataTableCellProps
};
